/**
 * Dimension source availability.
 *
 * A breakdown is only usable if the connected source actually returns a value
 * for it. Grouping by a dimension no platform supplies produces an empty chart
 * that reads as "your filters matched nothing" — which is a different problem
 * with a different fix, and the reason Bonneville reported FB-009.
 *
 * Availability is *measured from the returned rows* rather than declared in a
 * hardcoded list. A static list goes stale the moment an adapter starts
 * delivering a field; measuring means a dimension lights up on its own with no
 * code change. Ported from the same approach in flank-20
 * (`src/lib/dashboards/dimension-availability.ts`).
 *
 * Deliberately conservative: an empty result set is NOT a source limitation —
 * that is a filter with no matches, and claiming otherwise would tell partners
 * a working dimension is unsupported.
 */

/** True when at least one row carries a usable value for `key`. */
export const rowsCoverDimension = (rows: Array<Record<string, unknown>>, key: string): boolean =>
  rows.some(row => {
    const value = row[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });

/** Partner-facing explanation for a dimension the source doesn't return. */
export const dimensionSourceDetail = (label: string): string =>
  `${label} is not available from this data source. The connected reporting platform doesn't return this field, so there is nothing to group by.`;

/**
 * Returns the explanation when `key` cannot be grouped, or null when it can.
 *
 * `null` for an empty result set is intentional — see the module note.
 */
export const dimensionUnavailableDetail = (
  label: string,
  rows: Array<Record<string, unknown>>,
  key: string | undefined,
): string | null => {
  if (!key) return null;
  if (rows.length === 0) return null;
  if (rowsCoverDimension(rows, key)) return null;
  return dimensionSourceDetail(label);
};
